import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { sendFeedback } from '@apiCalls';
import { CtaContainer } from '@components/CtaContainer';
import { useAppContext } from '@contexts';
import {
  handleError,
  handleRenderError,
  withErrorHandler,
} from '@utils/errorHandler';
import tracker from '@utils/tracking';

interface IFeedbackFormProperties {
  onContinue: () => void;
}

export const FeedbackForm = ({ onContinue }: IFeedbackFormProperties) => {
  try {
    const [selectedStar, setSelectedStar] = useState<number | undefined>();
    const [feedbackStatus, setFeedbackStatus] = useState<
      'init' | 'success' | 'error'
    >('init');
    const {
      event,
      eventSeries,
      ticketDetails: { purchasedTickets },
    } = useAppContext();
    const sendFeedbackMutation = useMutation({
      mutationFn: sendFeedback as ({
        attendeeTicketKey,
        feedbackRating,
      }: ISendFeedbackParameters) => Promise<unknown>,
    });
    const radioOptions = [
      { icon: '😠', score: 1 },
      { icon: '🙁', score: 2 },
      { icon: '😊', score: 3 },
      { icon: '🤩', score: 4 },
    ];

    const handleFeedbackPress = async () => {
      if (purchasedTickets && selectedStar !== undefined) {
        try {
          tracker.track({
            event: {
              event: 'Feedback Submitted',
              category: 'Feedback',
              label: String(selectedStar),
              value: eventSeries?.ID ?? event.ID,
            },
            platforms: ['ga'],
          });
          await sendFeedbackMutation.mutateAsync({
            attendeeTicketKey: purchasedTickets[0].AttendeeTicketKey,
            feedbackRating: selectedStar,
          });
          setFeedbackStatus('success');
          // Show success message briefly before continuing
          setTimeout(() => {
            onContinue();
          }, 1000);
        } catch (error) {
          handleError(error, {
            inAsync: true,
            tags: { feature: 'feedback-form' },
          });
          setFeedbackStatus('error');
          // Continue after a brief delay to show the issue but not block the user
          setTimeout(() => {
            onContinue();
          }, 2000);
        }
      } else {
        // If no star selected or no tickets, just continue
        onContinue();
      }
    };

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleFeedbackPress();
    };

    return (
      <form className='feedbackContainer' onSubmit={handleFormSubmit}>
        <fieldset className='feedbackRatingContainer'>
          {radioOptions.map(option => (
            <div
              className={`feedbackRatingOption ${
                selectedStar === undefined
                  ? ''
                  : selectedStar === option.score
                  ? 'selected'
                  : 'muted'
              }`}
              key={option.score}
              onClick={withErrorHandler(() => setSelectedStar(option.score))}
            >
              <input
                type='radio'
                name='feedbackRating'
                id={`feedbackRatingScore-${option.score}`}
                value={option.score}
              />
              <label htmlFor={`feedbackRatingScore-${option.score}`}>
                <h1 className='feedbackRatingIcon'>{option.icon}</h1>
              </label>
            </div>
          ))}
        </fieldset>
        {feedbackStatus === 'success' && (
          <div
            style={{
              color: '#fff',
              textAlign: 'center',
              marginTop: '1rem',
            }}
          >
            Thank you for your feedback!
          </div>
        )}
        {feedbackStatus === 'error' && (
          <div
            style={{
              color: '#dc3545',
              textAlign: 'center',
              marginTop: '1rem',
            }}
          >
            We couldn't save your feedback, but you can continue to your ticket.
          </div>
        )}
        <CtaContainer
          blocked={!selectedStar}
          loading={sendFeedbackMutation.isPending}
          buttonText='Submit Feedback'
          onButtonClick={withErrorHandler(handleFeedbackPress)}
        />
      </form>
    );
  } catch (error) {
    return handleRenderError(error);
  }
};
